import { Injectable } from '@angular/core'
import { environment } from '../environments/environment'

type Event = 'LandedOnRegistration' | 'SelectedRole' | 'LoginInfoSubmitted' | 'PlanSelected' | 'PaymentInfoAdded' | 'Activated' | 'purchased';

type DataLayerObject = {
  userId?: number,
  userType?: 'business' | 'agency' | 'writer' | 'guest' | 'admin',
  event?: Event,
  eventCallback?: () => void,
  eventTimeout?: number,
  value?: number,
  accountId?: number,
  summary?: {
    words: string | number;
    credits: number;
  },
  ecommerce?: {
    currency: string,
    value: string | number,
    affiliation: string,
    transaction_id: string
  }
  paymentSuccessful?: boolean
};

type DataLayer = DataLayerObject[];

interface IUser {
  id: number;
  role: 'customer' | 'writer' | 'admin';
  account: {
    id: number;
    agency: boolean;
  };
}

const sendEvents = environment.production || environment.test || environment.demo

@Injectable({providedIn: 'root'})
export class AnalyticsActions {

  setUser(user: IUser): void {
    const dataLayer: DataLayer = window['dataLayer']
    if (sendEvents && dataLayer) {
      if (user) {
        const role = user.role === 'customer' ? user.account.agency ? 'agency' : 'business' : user.role
        dataLayer.push({
          userId: user.id,
          userType: role,
          accountId: user.account.id
        })
      } else {
        dataLayer.push({
          userId: null,
          userType: 'guest',
          accountId: null
        })
      }
    }
  }

  landedOnRegistration(): void {
    this.uniqueEvent({ event: 'LandedOnRegistration' })
  }

  // biz vs agency
  selectedRole(role: 'business' | 'agency'): void {
    this.uniqueEvent({
      event: 'SelectedRole',
      userType: role
    })
  }

  submittedLoginInfo(): void {
    this.uniqueEvent({ event: 'LoginInfoSubmitted' })
  }

  selectedPlan(): void {
    this.uniqueEvent({ event: 'PlanSelected' })
  }

  activated(accountId: number, value: number, userType: DataLayerObject['userType'], summary: any, paymentSuccessful: boolean): Promise<any> {
    this.uniqueEvent({
      event: 'PaymentInfoAdded',
      value,
      userType,
      summary,
      paymentSuccessful
    })
    if(paymentSuccessful) {
      this.uniqueEvent({
        event: 'purchased',
        ecommerce: {
          currency: 'USD',
          value: value,
          affiliation: userType,
          transaction_id: Date.now().toString()
        }
      })
    }
    return this.uniqueEvent({
      event: 'Activated',
      accountId,
      value
    })
  }

  // returns a 'done' promise
  // see https://www.simoahava.com/gtm-tips/use-eventtimeout-eventcallback/
  private uniqueEvent(eventObject: DataLayerObject): Promise<void> {
    const dataLayer: DataLayer = window['dataLayer']
    if (sendEvents && dataLayer) {
      const duplicate = dataLayer.find(o => o.event === eventObject.event)
      if (!duplicate) {
        return new Promise((resolve, reject) => {
          dataLayer.push(Object.assign({
            eventCallback: () => resolve(),
            eventTimeout: 1500 // trigger eventCallback after this time
          }, eventObject))
        })
      } else {
        return Promise.resolve()
      }
    } else {
      return Promise.resolve()
    }
  }

}
